import { env } from "@/env";
import { measuredFetch } from "@/metrics";

export function getXiServerUrl(): string {
  const url = env.NEXT_PUBLIC_XI_SERVER_URL;
  if (!url) {
    throw new Error("NEXT_PUBLIC_XI_SERVER_URL is not set");
  }
  return url;
}

const DEFAULT_TIMEOUT_MS = 10_000;

export interface XiFetchOptions {
  /** Extra headers merged on top of the default `Content-Type: application/json`. */
  headers?: Record<string, string>;
  method?: string;
  /** Key-value pairs appended to the URL as query parameters. */
  searchParams?: Record<string, string>;
  /** Caller-supplied abort signal (combined with the internal timeout). */
  signal?: AbortSignal;
  /** Request timeout in milliseconds. Defaults to 10 000 ms. */
  timeoutMs?: number;
}

/**
 * Thin wrapper around `fetch` for ElevenLabs XI API calls.
 *
 * Provides:
 * - Automatic base-URL resolution via `NEXT_PUBLIC_XI_SERVER_URL`
 * - Timeout via `AbortController` (combined with an optional caller signal)
 * - Structured error messages with response body extraction
 *
 * Returns the parsed JSON body as `unknown` so each caller can narrow the type.
 */
export async function xiFetch(path: string, options: XiFetchOptions = {}): Promise<unknown> {
  if (env.NEXT_PUBLIC_DISABLE_XI_FETCHES) {
    throw new Error("XI API fetches are disabled");
  }

  const { headers = {}, method = "GET", searchParams = {}, signal, timeoutMs = DEFAULT_TIMEOUT_MS } = options;

  const url = new URL(`${getXiServerUrl()}${path}`);
  for (const [key, value] of Object.entries(searchParams)) {
    url.searchParams.set(key, value);
  }

  // Abort handling — internal timeout + optional caller signal
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  const combinedSignal = signal ? AbortSignal.any([signal, controller.signal]) : controller.signal;

  try {
    const response = await measuredFetch(url.toString(), {
      method,
      headers: {
        "Content-Type": "application/json",
        ...headers,
      },
      signal: combinedSignal,
      cache: "no-store",
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const status = response.status;
      const statusText = response.statusText;
      const url = response.url;
      let errorBody: unknown = null;
      try {
        errorBody = await readErrorBody(response);
      } catch {
        // Body read failed; status/url are already captured above
      }
      throw new Error(`XI API error: ${status} ${statusText} (${url}). Body: ${JSON.stringify(errorBody)}`);
    }

    return await response.json();
  } catch (err) {
    clearTimeout(timeoutId);

    if (err instanceof Error && err.name === "AbortError") {
      throw new Error(`XI API request aborted after ${timeoutMs}ms`);
    }

    if (err instanceof Error) {
      throw err;
    }

    throw new Error("Unknown error during XI API request");
  }
}

export function isXiApiUrl(url: string): boolean {
  return url.includes(getXiServerUrl());
}

async function readErrorBody(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    try {
      return await response.text();
    } catch {
      return null;
    }
  }
}
